import { useMemo, useState } from 'react'
import { App, Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { GeoFilterBar } from '@/features/geo/GeoFilterBar'
import { GeoFormModal } from '@/features/geo/GeoFormModal'
import { geoColumns } from '@/features/geo/geoColumns'
import {
  ACTIVE_FILTER,
  SHOW_ALL,
  deactivateRegion,
  fetchRegionPage,
  updateRegion,
  type Region,
} from '@/features/geo/geoApi'

export function RegionsPage() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const { state, patch, reset } = useTableUrlState()
  const [editing, setEditing] = useState<Region | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const isFiltered = state.search !== '' || Object.keys(state.filters).length > 0

  /**
   * ⚠️ Bo'sh ro'yxat SUKUT bo'yicha ham chiqishi mumkin: nofaol
   * viloyatlar endi yashirin (`geoApi.ACTIVE_ONLY`). «Viloyat
   * qo'shilmagan» degan matn o'shanda YOLG'ON bo'lardi — hammasi
   * o'chirilgan bo'lsa ham xuddi shunday ko'rinardi. Shuning uchun
   * bo'sh holatda ikkinchi yo'l ham beriladi.
   */
  const showAll = () => patch({ page: 1, filters: { ...state.filters, [ACTIVE_FILTER]: [SHOW_ALL] } })

  const toggle = useMutation({
    mutationFn: async (row: Region) => {
      if (row.active) return deactivateRegion(row.id)
      await updateRegion(row.id, { name: row.name, code: row.code, active: true })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.geo.all })
      void message.success(t('common.toastSaved'))
    },
    onError: (error) => void message.error(translateError(error, t)),
  })

  const openForm = (row: Region | null) => {
    setEditing(row)
    setFormOpen(true)
  }

  const toggleActive = (row: Region) => {
    if (!row.active) {
      toggle.mutate(row)
      return
    }
    modal.confirm({
      title: t('geo.regions.deactivateTitle'),
      content: t('geo.regions.deactivateConfirm', { name: row.name }),
      okText: t('geo.deactivate'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () => toggle.mutate(row),
    })
  }

  const columns = useMemo(
    () =>
      geoColumns<Region>({
        t,
        onEdit: openForm,
        onToggleActive: toggleActive,
        pendingId: toggle.isPending ? (toggle.variables?.id ?? null) : null,
      }),
    // ⚠️ `openForm` va `toggleActive` ATAYLAB yo'q: ular har chizishda
    //    qayta yaratiladi va ro'yxatga qo'shilsa `useMemo` umuman
    //    ishlamay qolardi. Eskirmaydi — `openForm` faqat holat
    //    o'rnatuvchilarni, `toggleActive` esa barqaror `toggle.mutate`
    //    va `t` ni chaqiradi; `t` esa ro'yxatda BOR.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, toggle.isPending, toggle.variables],
  )

  return (
    <>
      <PageHeader
        title={t('nav.geoRegions')}
        description={t('pageIntro.geoRegions')}
        extra={
          <Can permission="geo.write">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
              {t('geo.regions.createTitle')}
            </Button>
          </Can>
        }
      />

      <DataTable<Region>
        queryKey={queryKeys.geo.regions}
        fetchPage={fetchRegionPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="geo-regions"
        stickyFirstColumn
        // Javobda `total` bo'lmasligi mumkin (`geoApi` sarlavhasi) — yasalgan
        // son aniq ko'rsatilsa YOLG'ON bo'lardi
        showTotal
        searchable
        searchPlaceholder={t('geo.searchPlaceholder')}
        toolbarExtra={<GeoFilterBar />}
        scrollX={860}
        {...(isFiltered
          ? {
              emptyTitle: t('dataTable.emptyFiltered'),
              emptyDescription: t('dataTable.emptyFilteredHint'),
              emptyAction: { label: t('dataTable.resetFilters'), onClick: reset },
            }
          : {
              emptyTitle: t('geo.regions.empty'),
              emptyDescription: t('geo.regions.emptyHint'),
              emptyAction: { label: t('dataTable.stateAll'), onClick: showAll },
            })}
      />

      <GeoFormModal
        open={formOpen}
        kind="region"
        entry={editing}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
      />
    </>
  )
}
